import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import type { ComponentProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import track, { useTracking } from 'react-tracking';
import type { TimeInterval } from 'd3-time';
import { LinePath } from '@visx/shape';
import { LoadingSpinner, Tab, useTheme } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';
import {
  getPriceChartTimeSeriesPeriodMap,
  getPeriodList,
} from './utils/periods';
import PriceChartEvent, {
  PriceChartEventLines,
} from '@/components/PriceChart/components/PriceChartEvent/PriceChartEvent';
import PriceChartPan from './PriceChartPan';
import PriceChartAxis from './PriceChartAxis';
import PriceChartMarker from './PriceChartMarker';
import PriceChartHeader from './PriceChartHeader';
import PriceChartMinMax from '@/components/PriceChart/components/PriceChartMinMax/PriceChartMinMax';
import PriceChartLegend, { filters } from './PriceChartLegend';
import { PriceChartArea } from './PriceChartArea';
import { PriceChartPeriodTrack } from './PriceChartPeriodTrack';
import {
  priceQueryFn,
  getQueryKey,
  eventsQueryFn,
} from '@/components/PriceChart/query';
import { cn } from '@simplywallst/ui-core/next';

const PriceChart = ({
  lookup,
  width = 320,
  height = 355,
  containerHeight = height + 100,
  currencyISO,
  children,
}: {
  lookup?: string;
  width?: number;
  height?: number;
  containerHeight?: number;
  currencyISO: string;
  children: (props: {
    periodMap: ReturnType<typeof getPriceChartTimeSeriesPeriodMap> | null;
    periodList: ReturnType<typeof getPeriodList> | null;
    period: number | null;
    currencyISO: string;
    isLoading: boolean;
    isPositiveTrend: boolean;
    priceChange: number;
    firstPrice: number;
    lastPrice: number;
  }) => React.ReactNode;
}) => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const [periodMap, setPeriodMap] = useState<ReturnType<
    typeof getPriceChartTimeSeriesPeriodMap
  > | null>(null);
  const [periodList, setPeriodList] = useState<ReturnType<
    typeof getPeriodList
  > | null>(null);
  const containerRef = useRef<null | HTMLDivElement>(null);
  const [period, setPeriod] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(width);
  const [tickFormat, setTickFormat] = useState<TimeInterval | null>(null);
  //remove categories there to toggle them off on chart load
  const [filteredEvents, setFilteredEvents] = useState(filters);
  const [tickFormatLabel, setTickFormatLabel] = useState<
    ((date: Date) => string) | ((date: number) => string) | null
  >(null);
  /** constants **/
  const hasTrackedDragRef = useRef(false); // For tracking
  const hasTrackedScrollRef = useRef(false); // For tracking
  const layoutHeaderHeight = 60;
  const layoutFooterHeight = 94;

  /** queries */
  const priceQuery = useQuery({
    queryKey: getQueryKey(
      lookup as string,
      period === periodMap?.MAX ? 0 : periodMap?.['3Y']
    ),
    queryFn: priceQueryFn,
    enabled: typeof lookup === 'string' && periodMap !== null,
    staleTime: Infinity,
  });

  const eventsQuery = useQuery({
    queryKey: [
      'events-history',
      lookup as string,
      periodMap?.['3Y'],
      filteredEvents,
    ],
    queryFn: eventsQueryFn,
    enabled: typeof lookup === 'string' && filteredEvents.length > 0,
    staleTime: Infinity,
  });

  /** derived data */
  /** used by event lines */
  const eventPriceHistory = useMemo(() => {
    if (
      typeof eventsQuery.data === 'undefined' ||
      typeof priceQuery.data === 'undefined'
    ) {
      return [];
    }
    return eventsQuery.data.eventHistory
      .map((event) => {
        if (typeof priceQuery.data === 'undefined') {
          return { date: event.date, close: null };
        }
        const close = priceQuery.data.prices.find(
          (price) => price.date === event.date
        );
        return { date: event.date, close: close?.close || null };
      })
      .filter((event) => event.close !== null);
  }, [eventsQuery.data, priceQuery.data]);

  const handlePeriodUpdate = useCallback(
    (period: number) => {
      if (!periodList) return;
      const nextPeriod = periodList.find((n) => n.period === period);
      if (typeof nextPeriod === 'undefined') return;
      const tickQuery = width < theme.smMaxRaw ? 1 : 0;
      setPeriod(period);
      setTickFormat(() => nextPeriod.tickFormat[tickQuery]);
      setTickFormatLabel(() => nextPeriod.tickLabelFormat[tickQuery]);
    },
    [periodList, theme, width]
  );

  const handleDoubleClick = useCallback(() => {
    if (periodList === null) return;
    trackEvent({ action: 'double_click' });
    //	User is zooming
    let nextIndex = periodList.findIndex((n) => n.period === period);
    //	If deltaY is present, this event is onWheel, else onDoubleClick
    nextIndex -= 1;
    //	Catch too low or too high
    if (nextIndex < 0) return;
    //	Set nextPeriod
    const nextPeriod = periodList[nextIndex].period;
    //	Catch max
    if (nextPeriod === Infinity) return;
    //
    handlePeriodUpdate(nextPeriod);
  }, [handlePeriodUpdate, periodList, period]);

  useEffect(() => {
    function handleWindowResize() {
      if (containerRef.current) {
        const container = containerRef.current;
        const currentWidth = container.offsetWidth;
        if (containerWidth === currentWidth) return;
        if (periodList === null) return;
        const nextPeriod = periodList.find(
          (periodListItem) => periodListItem.period === period
        );
        if (typeof nextPeriod === 'undefined') return;
        const tickQuery = currentWidth < theme.smMaxRaw ? 1 : 0;
        setContainerWidth(currentWidth);
        setTickFormat(() => nextPeriod.tickFormat[tickQuery]);
        setTickFormatLabel(() => nextPeriod.tickLabelFormat[tickQuery]);
      }
    }
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [periodList, containerWidth, period, theme.smMaxRaw]);

  useEffect(() => {
    const nextPeriodMap = getPriceChartTimeSeriesPeriodMap();
    const nextPeriodList = getPeriodList(nextPeriodMap);
    setPeriodMap(nextPeriodMap);
    setPeriodList(nextPeriodList);
  }, [lookup]);

  useEffect(() => {
    if (periodList === null) return;
    handlePeriodUpdate(period === null ? periodList[2].period : period);
  }, [period, periodList, handlePeriodUpdate]);

  function handleTabClick(event: TabOnClickEvent<number>) {
    const period = event.target.value;
    trackEvent({
      action: 'click',
      modifier: 'period',
      period:
        periodList !== null &&
        periodList.find((n) => n.period === Number(period))?.label,
    });
    handlePeriodUpdate(Number(period));
  }
  function handleDragMove() {
    if (!hasTrackedDragRef.current) {
      hasTrackedDragRef.current = true;
      trackEvent({ action: 'drag' });
    }
  }
  function handleScrollMove() {
    if (!hasTrackedScrollRef.current) {
      hasTrackedScrollRef.current = true;
      trackEvent({ action: 'scroll', modifier: 'timeline' });
    }
  }

  const handleEventOnClick = useCallback(
    (
      args: Parameters<ComponentProps<typeof PriceChartEvent>['onClick']>[0]
    ) => {
      trackEvent({
        action: 'click',
        modifier: 'event',
        event_id: args.id,
        event_date: args.date,
        event_categories: filteredEvents,
      });
    },
    [filteredEvents]
  );

  if (
    periodMap === null ||
    period === null ||
    tickFormatLabel === null ||
    periodList === null
  ) {
    return (
      <div
        className="grid min-h-[--container-height] place-items-center"
        style={
          {
            '--container-height': `${containerHeight}px`,
          } as React.CSSProperties
        }
      >
        <span className="mb-3">
          <LoadingSpinner light />
        </span>
      </div>
    );
  }

  const isLoading =
    priceQuery.status === 'pending' || eventsQuery.status === 'pending';

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'grid min-h-[--container-height] select-none gap-inherit transition-opacity duration-300 ease-in-out @container',
          isLoading && 'opacity-15'
        )}
        style={
          {
            '--container-height': `${containerHeight}px`,
          } as React.CSSProperties
        }
      >
        {typeof priceQuery.data !== 'undefined' &&
          priceQuery.data.prices.length > 0 && (
            <PriceChartPan
              now={periodMap.NOW}
              data={priceQuery.data.prices}
              dataInverse={priceQuery.data.inversePrices}
              width={containerWidth}
              height={height - layoutHeaderHeight - layoutFooterHeight}
              period={period}
              handleDragMove={handleDragMove}
              handleScrollMove={handleScrollMove}
              handleDoubleClick={handleDoubleClick}
            >
              {({
                dx,
                yMin,
                yMax,
                data,
                dataInverse,
                yScale,
                xScale,
                transform,
                selectedX,
                selectedY,
                xScaleWidth,
                selectedDate,
                selectedClose,
                isPositiveTrend,
                priceChange,
                lastPrice,
                firstPrice,
                handleDragEnd,
                handleDragMove,
                handleDragStart,
                handleScrollEnd,
                handleScrollMove,
                handleScrollStart,
                handleDoubleClick,
              }) => {
                return (
                  <div
                    className="grid gap-inherit"
                    style={
                      {
                        '--trend-color': isPositiveTrend
                          ? 'var(--s-color-good)'
                          : 'var(--s-color-bad)',
                        '--chart-line-color': 'var(--trend-color)',
                        '--chart-header-bg': 'var(--s-color-surface)',
                        '--chart-box-background-color': 'black',
                        '--chart-background-color': 'var(--s-color-surface-2)',
                        '--chart-marker-color': 'var(--trend-color)',
                        '--chart-point-line': 'var(--s-color-p-white-170)',
                        '--chart-axis-text': theme.color.white,
                      } as React.CSSProperties
                    }
                  >
                    {isLoading && (
                      <div className="absolute inset-0 z-50 grid place-items-center">
                        <LoadingSpinner light />
                      </div>
                    )}
                    <div className="grid items-center gap-2 @[100ch]:grid-cols-[minmax(0,1fr)_auto]">
                      {children?.({
                        periodMap,
                        periodList,
                        period,
                        currencyISO,
                        isLoading,
                        isPositiveTrend,
                        priceChange,
                        lastPrice,
                        firstPrice,
                      })}
                      <div className="grid w-full grid-flow-col @[100ch]:ml-auto @[100ch]:justify-end">
                        {periodList.map((n) => (
                          <Tab<number>
                            key={`price-chart-tab-${n.period}`}
                            value={n.period}
                            styleType="period"
                            selected={period === n.period}
                            onClick={handleTabClick}
                          >
                            <div className="pointer-events-none px-1.5 [button:hover_&]:text-p-white [button:hover_&]:duration-300">
                              <div aria-hidden="true">{n.label}</div>
                              <span className="sr-only">{n.labelAria}</span>
                            </div>
                          </Tab>
                        ))}
                      </div>
                    </div>
                    <PriceChartPeriodTrack
                      dx={dx}
                      data={data}
                      width={containerWidth}
                      disabled={period === Infinity}
                      xScaleWidth={xScaleWidth}
                      handleScrollEnd={handleScrollEnd}
                      handleScrollMove={handleScrollMove}
                      handleScrollStart={handleScrollStart}
                    />
                    <div className="relative overflow-hidden">
                      <PriceChartHeader
                        width={containerWidth}
                        currentX={(selectedX || 0) + dx}
                        currencyISO={currencyISO}
                        currentDate={selectedDate}
                        currentClose={selectedClose}
                      />
                      <svg
                        width={containerWidth}
                        height={height}
                        xmlns="http://www.w3.org/2000/svg"
                        className="block"
                      >
                        <g transform={`translate(${transform}, 0)`}>
                          <PriceChartEventLines
                            data={eventPriceHistory}
                            top={layoutHeaderHeight}
                            dx={dx}
                            containerWidth={containerWidth}
                            height={height - layoutHeaderHeight - 52}
                            xScale={xScale}
                            yScale={yScale}
                          />
                          {xScale && (
                            <PriceChartAxis
                              top={height - layoutFooterHeight}
                              width={containerWidth}
                              xScale={xScale}
                              numTicks={
                                tickFormat ? xScale.ticks(tickFormat).length : 0
                              }
                            />
                          )}
                          <g transform={`translate(0, ${layoutHeaderHeight})`}>
                            {dataInverse.length > 0 && (
                              <LinePath
                                x={(d) => xScale(d.date) as number}
                                y={(d) =>
                                  d.close ? (yScale(d.close) as number) : 0
                                }
                                data={dataInverse}
                                stroke={theme.color.darkGray}
                                defined={(d) => d.close !== null}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeDasharray="2, 3"
                              />
                            )}
                            {/* Main line */}
                            <LinePath
                              x={(d) => xScale(d.date) as number}
                              y={(d) =>
                                d.close ? (yScale(d.close) as number) : 0
                              }
                              data={data}
                              stroke="var(--chart-line-color)"
                              defined={(d) => d.close !== null}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            {/* Main line gradient */}
                            <PriceChartArea
                              data={data}
                              xScale={xScale}
                              yScale={yScale}
                              fill="var(--chart-line-color)"
                              fillOpacity={0.075}
                            />
                            {selectedX && selectedY && (
                              <PriceChartMarker
                                x={selectedX}
                                y={selectedY}
                                width={containerWidth}
                                height={height}
                                // height={height - layoutHeaderHeight}
                                // height={
                                //   height -
                                //   layoutHeaderHeight -
                                //   layoutFooterHeight
                                // }
                                dx={dx}
                              />
                            )}
                          </g>
                          {typeof eventsQuery.data !== 'undefined' &&
                            period < Infinity && (
                              <PriceChartEvent
                                dx={dx}
                                top={layoutHeaderHeight}
                                eventHistory={eventsQuery.data.eventHistory}
                                height={height - layoutHeaderHeight - 52}
                                xScale={xScale}
                                yScale={yScale}
                                onClick={handleEventOnClick}
                                containerWidth={containerWidth}
                              />
                            )}
                        </g>
                        <PriceChartMinMax
                          top={layoutHeaderHeight}
                          max={yMax || 0}
                          min={yMin || 0}
                          bottom={height - layoutFooterHeight - 8}
                          currencyISO={currencyISO}
                        />
                        <g
                          // transform={`translate(0, 0)`}
                          // transform={`translate(0, ${layoutHeaderHeight})`}
                          data-cy-id="price-chart-drag-area"
                        >
                          <rect
                            className="cursor-grab active:cursor-grabbing"
                            fill="transparent"
                            width={containerWidth}
                            height={height - layoutFooterHeight + 20}
                            // height={
                            //   height - layoutFooterHeight - layoutHeaderHeight
                            // }
                            onMouseUp={handleDragEnd}
                            onTouchEnd={handleDragEnd}
                            onTouchMove={handleDragMove}
                            onMouseMove={handleDragMove}
                            onMouseDown={handleDragStart}
                            onMouseLeave={handleDragEnd}
                            onTouchStart={handleDragStart}
                            onDoubleClick={handleDoubleClick}
                          />
                        </g>
                      </svg>
                    </div>
                  </div>
                );
              }}
            </PriceChartPan>
          )}
      </div>
      {period < Infinity && (
        <div className="-mx-5 bg-surface-1 p-2">
          <div
            className={cn(
              'text-center transition-opacity duration-300 ease-in-out',
              isLoading && 'opacity-15'
            )}
          >
            <PriceChartLegend
              toggledFilters={filteredEvents}
              onUpdate={(filters) => {
                trackEvent({
                  action: 'toggle',
                  modifier: 'event_filter',
                  active: filters,
                });
                setFilteredEvents(filters);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

/**
 * @deprecated The new narrative chart is going to replace this component.
 */
const PriceChartWithTracking = track({
  type: 'track',
  subject: 'price_chart',
})(PriceChart) as typeof PriceChart;

export { PriceChartWithTracking as PriceChart };
